import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empAddressService } from '@/services/empAddressService'
import { employeesService } from '@/services/employeesService'
import { customersService } from '@/services/customersService'
import type { Employee, CountryLookup, CityLookup, EmpAddressVm } from '@/api/types'
import { showError, showSuccess } from '@/lib/swal'

export const EmpAddressEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])

  const [form, setForm] = useState({
    empId: 0,
    zipCode: '',
    poBox: '',
    countryId: 0,
    cityId: 0,
    district: '',
    street: '',
    building: '',
    apartment: '',
    homePhoneNumber: '',
    addressDetail: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const empRes = await employeesService.getAllEmployees(branchId, 1, 500)
        setEmployees(empRes.data ?? [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryRes = await customersService.getCountries()
        setCountries(countryRes ?? [])
      } catch {
        setCountries([])
      }
    }
    loadCountries()
  }, [])

  useEffect(() => {
    if (form.countryId && form.countryId > 0) {
      const loadCities = async () => {
        try {
          const citiesData = await customersService.getCities(form.countryId)
          setCities(citiesData ?? [])
        } catch {
          setCities([])
        }
      }
      loadCities()
    } else {
      setCities([])
    }
  }, [form.countryId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empAddressService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          zipCode: String(data.zipCode ?? ''),
          poBox: String(data.poBox ?? ''),
          countryId: data.countryId ?? 0,
          cityId: data.cityId ?? 0,
          district: data.district ?? '',
          street: data.street ?? '',
          building: data.building ?? '',
          apartment: data.apartment ?? '',
          homePhoneNumber: String(data.homePhoneNumber ?? ''),
          addressDetail: data.addressDetail ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load address.')
        navigate('/emp-addresses')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn]
          .filter(Boolean)
          .join(' ')
          .trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id), label }
      }),
    [employees]
  )

  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        value: String(c.id ?? 0),
        label: c.lookupNameEn ?? c.lookupNameAr ?? `Country ${c.id}`,
      })),
    [countries]
  )

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: String(c.id ?? 0),
        label: c.lookupNameEn ?? c.lookupNameAr ?? `City ${c.id}`,
      })),
    [cities]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    if (!form.homePhoneNumber.trim()) {
      toast.error('Home Phone Number is required.')
      return
    }
    if (!form.addressDetail.trim()) {
      toast.error('Address Detail is required.')
      return
    }

    const toNumberOrZero = (value: string) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    }

    const payload: EmpAddressVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      zipCode: toNumberOrZero(form.zipCode),
      poBox: toNumberOrZero(form.poBox),
      countryId: form.countryId || 0,
      cityId: form.cityId || 0,
      district: form.district,
      street: form.street,
      building: form.building,
      apartment: form.apartment,
      homePhoneNumber: toNumberOrZero(form.homePhoneNumber),
      addressDetail: form.addressDetail,
      branchId: branchId ?? 0,
    }

    console.log('🔵 [EmpAddress] Save payload:', payload)

    try {
      setLoading(true)
      if (isEditing) {
        await empAddressService.update(payload)
        await showSuccess('Updated', 'Address updated successfully.')
      } else {
        await empAddressService.create(payload)
        await showSuccess('Saved', 'Address saved successfully.')
      }
      navigate('/emp-addresses')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save address.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Employee Address" description="Add or edit employee address." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Address"
        description={isEditing ? 'Edit employee address' : 'Add new employee address'}
      />

      <Card>
        <CardHeader>
          <CardTitle>Address</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
              />
            </div>
            <div className="space-y-2">
              <Label>Zip Code *</Label>
              <Input
                value={form.zipCode}
                onChange={(e) => handleChange({ zipCode: e.target.value })}
                placeholder="Zip Code"
              />
            </div>
            <div className="space-y-2">
              <Label>PoBox *</Label>
              <Input
                value={form.poBox}
                onChange={(e) => handleChange({ poBox: e.target.value })}
                placeholder="PoBox"
              />
            </div>
            <div className="space-y-2">
              <Label>Country *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Country' }, ...countryOptions]}
                value={form.countryId ? String(form.countryId) : '0'}
                onValueChange={(v) => handleChange({ countryId: Number(v) || 0, cityId: 0 })}
                placeholder="Country"
                searchPlaceholder="Search country..."
                emptyMessage="No country found"
              />
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'City' }, ...cityOptions]}
                value={form.cityId ? String(form.cityId) : '0'}
                onValueChange={(v) => handleChange({ cityId: Number(v) || 0 })}
                placeholder="City"
                searchPlaceholder="Search city..."
                emptyMessage="No city found"
                disabled={!form.countryId || form.countryId === 0}
              />
            </div>
            <div className="space-y-2">
              <Label>District *</Label>
              <Input
                value={form.district}
                onChange={(e) => handleChange({ district: e.target.value })}
                placeholder="District"
              />
            </div>
            <div className="space-y-2">
              <Label>Street*</Label>
              <Input
                value={form.street}
                onChange={(e) => handleChange({ street: e.target.value })}
                placeholder="Street"
              />
            </div>
            <div className="space-y-2">
              <Label>Building*</Label>
              <Input
                value={form.building}
                onChange={(e) => handleChange({ building: e.target.value })}
                placeholder="Building"
              />
            </div>
            <div className="space-y-2">
              <Label>Apartment*</Label>
              <Input
                value={form.apartment}
                onChange={(e) => handleChange({ apartment: e.target.value })}
                placeholder="Apartment"
              />
            </div>
            <div className="space-y-2">
              <Label>Home Phone Number *</Label>
              <Input
                value={form.homePhoneNumber}
                onChange={(e) => handleChange({ homePhoneNumber: e.target.value })}
                placeholder="Home Phone Number"
              />
            </div>
            <div className="space-y-2 lg:col-span-2">
              <Label>Address Detail *</Label>
              <Input
                value={form.addressDetail}
                onChange={(e) => handleChange({ addressDetail: e.target.value })}
                placeholder="AddressDetail"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/emp-addresses')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? 'Update' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

